import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { useContent } from "@/lib/content";
import GlitchText from "@/theme/GlitchText";
import Button from "@/theme/Button";
import Pill from "@/theme/Pill";
import Rosette from "@/theme/Rosette";

const Hero = () => {
  const { siteContent, sections } = useContent();
  const { hero } = siteContent;
  const studiosShipped = (sections?.clients?.items || []).filter((i) => i.enabled).length;

  // Split hero.title by <br/> and apply alternating glitch colors.
  const parts = (hero?.title || "").split(/<br\s*\/?\s*>/i);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative z-10 pt-12 md:pt-20 pb-24 md:pb-28"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-7">
          <Pill color="#c8ff2a" tilt={-2}>
            ✦ {hero?.tagline || "Game Studio"}
          </Pill>
          <Pill color="#ff2a8f" tilt={1}>
            Atelier · 2026
          </Pill>
        </div>

        <h1
          className="font-black uppercase"
          style={{
            fontFamily: "'Azeret Mono', monospace",
            letterSpacing: "-0.06em",
            lineHeight: 0.84,
            fontSize: "clamp(3.2rem, 11vw, 11rem)",
          }}
          data-testid="hero-title"
        >
          {parts.map((p, i) => {
            const defaultColors = ["#c8ff2a", "#ff2a8f", "#2ad6ff", "#ffffff"];
            const lineColors = hero?.lineColors || defaultColors;
            const color = lineColors[i] || defaultColors[i % defaultColors.length];
            const italic = i === 1;
            return (
              <span key={i} className="block">
                <GlitchText
                  color={color}
                  intensity={i === 1 ? 0.8 : 1.2}
                  style={{
                    fontStyle: italic ? "italic" : "normal",
                    paddingLeft: i === 1 ? "6vw" : 0,
                    textShadow: `0 0 40px ${color}66`,
                  }}
                >
                  {p.replace(/<[^>]+>/g, "")}
                </GlitchText>
              </span>
            );
          })}
        </h1>

        <div className="mt-12 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <p
              className="text-base md:text-lg leading-relaxed max-w-xl"
              style={{ color: "#cfcfc4" }}
            >
              {hero?.subtitle}
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-8">
              <Button as={Link} to="/contact" data-testid="hero-cta-primary">
                {hero?.ctaPrimary || "Start a project"}
                <ArrowRight size={14} strokeWidth={3} />
              </Button>
              <Button
                as={Link}
                to="/work"
                variant="outline"
                data-testid="hero-cta-secondary"
              >
                <Play size={13} strokeWidth={3} />
                {hero?.ctaSecondary || "See our games"}
              </Button>
            </div>
          </div>

          {/* Rosette */}
          <div className="md:col-span-5 flex md:justify-end">
            <Rosette
              big={String(studiosShipped || 3).padStart(2, "0")}
              topLabel="shipped for"
              bottomLabel="studios"
              size={224}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
